import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AdfDocument } from './agentMentionUtils.ts';
import {
	extractAgentMentionFromAdf,
	extractUnresolvedMentionIds,
	resolveAgentFromMentionId,
} from './agentMentionUtils.ts';

export type AgentInfo = {
	id: string;
	name: string;
};

/**
 * Hook that tracks whether the current comment ADF contains an agent mention,
 * intended to drive the visibility and content of the FYI nudge above the editor.
 *
 * Detection is two-tier:
 *   1. Fast path (sync) — a freshly-typed mention still carries `userType==='APP'`,
 *      so it is detected immediately with no network call.
 *   2. Fallback (async, cached) — a pasted/persisted mention has lost `userType`
 *      (the backend strips it on the ADF→storage→ADF round-trip), so each surviving
 *      mention `id` is resolved against the Rovo agents endpoint. Results are cached
 *      per id, so each distinct id is fetched at most once per editor session: typing
 *      after a paste is free, and new ids only enter the doc via paste/insert — never
 *      via keystrokes — so network calls are bounded by distinct mentions, not edits.
 *
 * Call `onContentChange` from the editor's content-change callback.
 * Read `showFyi` to decide whether to render `<AgentMentionFyiNudge />`.
 * Read `agent` for the mentioned agent's identity.
 *
 * `cloudId` is required to enable the async fallback; without it only the fast
 * path runs (matching the previous behaviour).
 */
export function useAgentMentionFyiNudge({
	enabled = true,
	cloudId,
}: { enabled?: boolean; cloudId?: string } = {}): {
	showFyi: boolean;
	agent: AgentInfo | undefined;
	onContentChange: (adf: AdfDocument | undefined) => void;
} {
	const [showFyi, setShowFyi] = useState(false);
	const [agent, setAgent] = useState<AgentInfo | undefined>();

	// Per-editor cache of mention-id → resolved agent identity (or null when the id
	// is not an agent). Guarantees each distinct id hits the agents endpoint at most
	// once. `inFlight` dedupes concurrent resolutions for the same id.
	const cacheRef = useRef<Map<string, AgentInfo | null>>(new Map());
	const inFlightRef = useRef<Set<string>>(new Set());
	const abortControllersRef = useRef<Set<AbortController>>(new Set());
	// Ids of the latest non-fast-path mention candidates, so async resolutions can
	// re-evaluate visibility against the current doc.
	const latestCandidateIdsRef = useRef<string[]>([]);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		// Capture the (stable, never-reassigned) ref-held collections so the cleanup
		// operates on the same objects without reading `.current` at teardown
		// (satisfies react-hooks/exhaustive-deps).
		const abortControllers = abortControllersRef.current;
		const inFlight = inFlightRef.current;
		return () => {
			mountedRef.current = false;
			abortControllers.forEach((controller) => controller.abort());
			abortControllers.clear();
			inFlight.clear();
		};
	}, []);

	// Re-derive showFyi/agent from the cache for the current doc's mention ids.
	const applyFromCache = useCallback(() => {
		const knownAgent = latestCandidateIdsRef.current
			.map((id) => cacheRef.current.get(id))
			.find((value): value is AgentInfo => !!value);

		setShowFyi(!!knownAgent);
		setAgent((prev) => {
			if (!knownAgent) {
				return undefined;
			}
			return prev?.id === knownAgent.id && prev?.name === knownAgent.name ? prev : knownAgent;
		});
	}, []);

	const onContentChange = useCallback(
		(adf: AdfDocument | undefined) => {
			if (!enabled) {
				return;
			}

			// Fast path — a freshly-typed agent mention still carries userType==='APP'.
			const fastPath = extractAgentMentionFromAdf(adf);
			if (fastPath) {
				latestCandidateIdsRef.current = [];
				setShowFyi(true);
				setAgent((prev) =>
					prev?.id === fastPath.agentId && prev?.name === fastPath.agentName
						? prev
						: { id: fastPath.agentId, name: fastPath.agentName },
				);
				return;
			}

			// Fallback — userType may have been stripped (paste / reloaded comment).
			// Only mentions that have LOST their userType need id-resolution; live
			// mentions carry a userType (APP is handled by the fast path above, any
			// other type is not an agent), so they are skipped to avoid wasted calls.
			const candidateIds = extractUnresolvedMentionIds(adf);
			latestCandidateIdsRef.current = candidateIds;

			// Reflect anything already cached immediately (no flicker, no refetch).
			applyFromCache();

			// The async fallback needs a cloudId to call the agents endpoint.
			if (!cloudId) {
				return;
			}

			for (const id of candidateIds) {
				if (cacheRef.current.has(id) || inFlightRef.current.has(id)) {
					continue; // already resolved, or a resolution is already in flight
				}
				inFlightRef.current.add(id);
				const controller = new AbortController();
				abortControllersRef.current.add(controller);
				void resolveAgentFromMentionId(id, cloudId, controller.signal)
					.then((resolved) => {
						abortControllersRef.current.delete(controller);
						inFlightRef.current.delete(id);
						if (!mountedRef.current) {
							return;
						}
						cacheRef.current.set(id, resolved);
						applyFromCache();
					})
					.catch(() => {
						// resolveAgentFromMentionId is not expected to reject (it catches
						// internally), but guard defensively so a future refactor — or a
						// throw from applyFromCache — can't surface an unhandled rejection
						// or leave the id stuck in-flight. The nudge is best-effort.
						abortControllersRef.current.delete(controller);
						inFlightRef.current.delete(id);
					});
			}
		},
		[enabled, cloudId, applyFromCache],
	);

	return useMemo(() => ({ showFyi, agent, onContentChange }), [showFyi, agent, onContentChange]);
}
